import { lazy, Suspense, useState } from "react";
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Spin, message } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ListPage } from "../../components/ListPage";
import { useStockLocationsQuery, useItemsQuery } from "../../helpers/queries";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { StockBalance } from "../../api-models";

const OpeningBalanceImportDialog = lazy(() => import("./OpeningBalanceImportDialog"));

export default function StockRegisterPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { data: locations = [] } = useStockLocationsQuery();
  const { data: items = [] } = useItemsQuery();
  const { request, loading } = useApi();
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);
  const canOpeningBalance = hasAnyGrant(user, "Stock.Adjust");
  const canAdjust = hasAnyGrant(user, "Adjustments.Write");

  // The only direct-post stock action left: a one-time physical-count cutover
  // (spec §6.4/§31.22). Everything ongoing — damage, wastage, loss, corrections — goes
  // through the approved Stock Adjustment document instead.
  async function postOpeningBalance() {
    const values = await form.validateFields();
    await request({
      method: "POST",
      url: "/stock/opening-balance",
      data: { ...values, cutoverDate: values.cutoverDate?.format("YYYY-MM-DD") },
    });
    message.success("Opening balance posted.");
    setOpen(false);
    setReloadKey((k) => k + 1);
  }

  return (
    <>
      <ListPage<StockBalance>
        key={reloadKey}
        title="Stock Register"
        endpoint="/stock"
        searchPlaceholder="Search by item..."
        filters={({ num, set }) => (
          <Select
            allowClear
            placeholder="Location"
            style={{ width: 240 }}
            options={locations.map((w) => ({ value: w.id, label: w.siteId ? w.name : `${w.name} (central)` }))}
            value={num("locationId")}
            onChange={(v) => set("locationId", v)}
          />
        )}
        actions={
          <Space>
            {canAdjust && <Button onClick={() => navigate("/adjustments/new")}>New adjustment</Button>}
            {canOpeningBalance && (
              <>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setOpen(true);
                  }}
                >
                  Opening balance
                </Button>
                {/* A go-live count is a sheet, not a form: the same posting, taken in bulk
                    from a material code and a counted quantity per row. */}
                <Button icon={<ImportOutlined />} onClick={() => setImporting(true)}>
                  Import balances
                </Button>
              </>
            )}
          </Space>
        }
        columns={[
          { title: "Location", render: (_, r) => r.location?.name },
          { title: "Code", render: (_, r) => r.item?.code },
          { title: "Item", render: (_, r) => r.item?.name },
          { title: "Available", key: "available", sorter: true, render: (_, r) => r.onHandQty - r.reservedQty },
          { title: "Reserved", dataIndex: "reservedQty", sorter: true },
          { title: "On hand", dataIndex: "onHandQty", sorter: true },
          { title: "Reorder level", render: (_, r) => r.item?.reorderLevel },
        ]}
      />
      <Modal
        title="Post opening balance"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={postOpeningBalance}
        confirmLoading={loading}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cutover only"
          description="This posts stock straight to the ledger, so it is for loading a physical count at go-live. To correct stock afterwards, raise a Stock Adjustment — it needs approval and records who authorised it."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="locationId" label="Location" rules={[{ required: true }]}>
            <Select options={locations.map((w) => ({ value: w.id, label: w.name }))} />
          </Form.Item>
          <Form.Item name="itemId" label="Item" rules={[{ required: true }]}>
            <Select
              showSearch
              options={items.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="qty" label="Counted quantity" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="cutoverDate" label="Cutover date" rules={[{ required: true }]} extra="One posting per item, per location, per cutover.">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} placeholder="e.g. Physical count at go-live, counted by …" />
          </Form.Item>
        </Form>
      </Modal>
      {importing && (
        <Suspense fallback={<Spin fullscreen />}>
          <OpeningBalanceImportDialog
            onClose={() => setImporting(false)}
            onImported={() => {
              setImporting(false);
              setReloadKey((k) => k + 1);
            }}
          />
        </Suspense>
      )}
    </>
  );
}
